/**
 * Driver Performance Dashboard (admin)
 * ------------------------------------
 * Aggregates the last N days of delivered orders and surfaces three views:
 *   1. Team scorecard — fleet-wide averages
 *   2. Per-driver leaderboard table with avg warehouse delay, idle, and
 *      GPS-accuracy %.
 *   3. Delivery-accuracy map — every drop-off pinned, colored by how
 *      far the driver stamped from the customer's shared pin.
 *
 * Read-only. Powered by:
 *   GET /api/driver-performance/admin?days=N
 *   GET /api/driver-performance/accuracy-map?days=N
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Truck, Clock, Crosshair, Users, RefreshCw, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useGoogleMapsLoader } from '@/components/GoogleMapView';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BUCKET_COLOR = { good: '#22c55e', ok: '#eab308', off: '#ef4444' };
const BUCKET_LABEL = { good: '< 30m', ok: '30–100m', off: '> 100m' };

const accuracyPinIcon = (bucket) =>
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">` +
      `<circle cx="11" cy="11" r="8" fill="${BUCKET_COLOR[bucket] || '#9ca3af'}" stroke="white" stroke-width="2"/>` +
      `</svg>`
  );

const DriverPerformancePage = () => {
  const [days, setDays] = useState(7);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [range, setRange] = useState(null); // applied custom range {from, to}
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ drivers: [] });
  const [points, setPoints] = useState([]);
  const [activePoint, setActivePoint] = useState(null);
  const { isLoaded: mapsLoaded } = useGoogleMapsLoader();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const query = range
        ? `date_from=${range.from}&date_to=${range.to}`
        : `days=${days}`;
      const [perf, acc] = await Promise.all([
        axios.get(`${API_BASE_URL}/driver-performance/admin?${query}`, { headers }),
        axios.get(`${API_BASE_URL}/driver-performance/accuracy-map?${query}`, { headers }),
      ]);
      setData(perf.data || { drivers: [] });
      setPoints(acc.data?.points || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, range]);

  // Fleet averages — collapse the per-driver metrics into a single
  // weighted-mean row.
  const fleet = useMemo(() => {
    const drivers = data.drivers || [];
    if (!drivers.length) return null;
    const sum = (k) => drivers.reduce((a, d) => a + (Number(d[k]) || 0), 0);
    const denom = (k) => drivers.filter((d) => d[k] != null).length || 1;
    const totalDeliveries = drivers.reduce((a, d) => a + (d.deliveries_count || 0), 0);
    const goodPctVals = drivers.map((d) => d.accuracy_distribution?.good_pct).filter((v) => v != null);
    return {
      driver_count: drivers.length,
      total_deliveries: totalDeliveries,
      avg_warehouse: sum('avg_warehouse_delay_min') / denom('avg_warehouse_delay_min'),
      avg_idle: sum('avg_inter_stop_idle_min') / denom('avg_inter_stop_idle_min'),
      avg_duration: sum('avg_delivery_duration_min') / denom('avg_delivery_duration_min'),
      avg_good_pct: goodPctVals.length ? goodPctVals.reduce((a, b) => a + b, 0) / goodPctVals.length : null,
    };
  }, [data]);

  const mapCenter = points.length
    ? { lat: points[0].lat, lng: points[0].lng }
    : { lat: 25.2048, lng: 55.2708 };

  return (
    <div className="space-y-6" data-testid="driver-performance-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Driver Performance</h1>
          <p className="text-wt-text-muted">
            Idle time, delivery duration, and GPS-pin accuracy{' '}
            {range ? `from ${range.from} to ${range.to}` : `over the last ${days} days`}.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[7, 14, 30].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={!range && days === d ? 'default' : 'outline'}
              onClick={() => { setRange(null); setDateFrom(''); setDateTo(''); setDays(d); }}
              data-testid={`window-${d}-btn`}
              className={!range && days === d ? 'bg-wt-gold text-black hover:bg-wt-gold/90' : ''}
            >
              {d}d
            </Button>
          ))}
          <div className="flex items-center gap-1.5" data-testid="date-range-filter">
            <DateInput
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 px-2 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-xs"
              data-testid="date-from-input"
            />
            <span className="text-wt-text-muted text-xs">to</span>
            <DateInput
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 px-2 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-xs"
              data-testid="date-to-input"
            />
            <Button
              size="sm"
              variant={range ? 'default' : 'outline'}
              disabled={!dateFrom || !dateTo || dateFrom > dateTo}
              onClick={() => setRange({ from: dateFrom, to: dateTo })}
              data-testid="apply-date-range-btn"
              className={range ? 'bg-wt-gold text-black hover:bg-wt-gold/90' : ''}
            >
              Apply
            </Button>
            {range && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setRange(null); setDateFrom(''); setDateTo(''); }}
                data-testid="clear-date-range-btn"
                className="text-wt-text-muted px-2"
              >
                ✕
              </Button>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Fleet scorecard */}
      {fleet && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Scorecard icon={<Users className="w-5 h-5" />} label="Drivers" value={fleet.driver_count} />
          <Scorecard icon={<Truck className="w-5 h-5" />} label="Deliveries" value={fleet.total_deliveries} />
          <Scorecard
            icon={<Clock className="w-5 h-5 text-amber-400" />}
            label="Avg Warehouse Delay"
            value={fleet.avg_warehouse != null ? `${fleet.avg_warehouse.toFixed(1)} min` : '—'}
          />
          <Scorecard
            icon={<Clock className="w-5 h-5 text-red-400" />}
            label="Avg Idle Between Stops"
            value={fleet.avg_idle != null ? `${fleet.avg_idle.toFixed(1)} min` : '—'}
          />
          <Scorecard
            icon={<Crosshair className="w-5 h-5 text-emerald-400" />}
            label="GPS Accurate (<30m)"
            value={fleet.avg_good_pct != null ? `${fleet.avg_good_pct.toFixed(0)}%` : '—'}
          />
        </div>
      )}

      {/* Per-driver leaderboard */}
      <Card className="bg-wt-surface border-wt-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="driver-leaderboard">
              <thead className="bg-wt-bg/60">
                <tr className="text-left text-wt-text-muted">
                  <th className="p-3 font-medium">Driver</th>
                  <th className="p-3 font-medium text-center">Deliveries</th>
                  <th className="p-3 font-medium text-center">Warehouse Delay</th>
                  <th className="p-3 font-medium text-center">Idle / Stop</th>
                  <th className="p-3 font-medium text-center">Delivery Time</th>
                  <th className="p-3 font-medium text-center">Accuracy (&lt;30m)</th>
                </tr>
              </thead>
              <tbody>
                {(data.drivers || []).map((d) => (
                  <tr
                    key={d.driver_id}
                    className="border-t border-wt-border hover:bg-wt-subtle/40"
                    data-testid={`driver-row-${d.driver_id}`}
                  >
                    <td className="p-3 text-wt-text-primary font-medium">
                      {d.driver_name || d.driver_id}
                    </td>
                    <td className="p-3 text-center text-wt-text-secondary">{d.deliveries_count}</td>
                    <td className="p-3 text-center">{fmtMins(d.avg_warehouse_delay_min, 15)}</td>
                    <td className="p-3 text-center">{fmtMins(d.avg_inter_stop_idle_min, 5)}</td>
                    <td className="p-3 text-center text-wt-text-secondary">
                      {d.avg_delivery_duration_min != null ? `${d.avg_delivery_duration_min.toFixed(1)} min` : '—'}
                    </td>
                    <td className="p-3 text-center">{fmtPct(d.accuracy_distribution?.good_pct)}</td>
                  </tr>
                ))}
                {!loading && !(data.drivers || []).length && (
                  <tr>
                    <td className="p-6 text-center text-wt-text-muted" colSpan={6}>
                      No delivered orders in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Delivery accuracy heatmap */}
      <Card className="bg-wt-surface border-wt-border overflow-hidden">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-wt-border flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-wt-gold" />
              <h2 className="text-sm font-semibold text-wt-text-primary">Delivery Accuracy Map</h2>
              <span className="text-xs text-wt-text-muted">({points.length} stamps)</span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              {['good', 'ok', 'off'].map((b) => (
                <div key={b} className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ background: BUCKET_COLOR[b] }}
                  />
                  <span className="text-wt-text-secondary">{BUCKET_LABEL[b]}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="h-[460px]" data-testid="accuracy-map">
            {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
              <div className="h-full w-full flex items-center justify-center text-xs text-wt-text-muted">
                Google Maps API key not configured
              </div>
            ) : !mapsLoaded ? (
              <div className="h-full w-full flex items-center justify-center text-sm text-wt-text-muted">
                Loading map…
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter}
                zoom={11}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                {points.map((p, i) => (
                  <Marker
                    key={`${p.order_id}-${i}`}
                    position={{ lat: p.lat, lng: p.lng }}
                    icon={{ url: accuracyPinIcon(p.bucket), scaledSize: new window.google.maps.Size(22, 22) }}
                    onClick={() => setActivePoint(`${p.order_id}-${i}`)}
                  >
                    {activePoint === `${p.order_id}-${i}` && (
                      <InfoWindow onCloseClick={() => setActivePoint(null)}>
                        <div className="text-xs text-gray-900">
                          <p className="font-semibold">#{p.order_number}</p>
                          <p>
                            {p.distance_m.toFixed(0)}m from customer pin{' '}
                            <Badge
                              className="ml-1"
                              style={{ background: BUCKET_COLOR[p.bucket], color: 'white' }}
                            >
                              {BUCKET_LABEL[p.bucket]}
                            </Badge>
                          </p>
                          <p className="text-gray-500">
                            {p.delivered_at ? new Date(p.delivered_at).toLocaleString() : ''}
                          </p>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
              </GoogleMap>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Scorecard = ({ icon, label, value }) => (
  <Card className="bg-wt-surface border-wt-border">
    <CardContent className="p-4 flex items-center gap-3">
      <div className="p-2 rounded-full bg-wt-bg/60">{icon}</div>
      <div>
        <p className="text-lg font-bold text-wt-text-primary">{value}</p>
        <p className="text-xs text-wt-text-muted">{label}</p>
      </div>
    </CardContent>
  </Card>
);

// Threshold-aware formatter: highlight the cell red if the metric is over
// the threshold the watchdog uses to fire warnings.
const fmtMins = (val, threshold) => {
  if (val == null) return <span className="text-wt-text-muted">—</span>;
  const over = val > threshold;
  return (
    <span className={`font-semibold ${over ? 'text-red-400' : 'text-emerald-300'}`}>
      {val.toFixed(1)} min
    </span>
  );
};

const fmtPct = (val) => {
  if (val == null) return <span className="text-wt-text-muted">—</span>;
  const good = val >= 70;
  return (
    <span className={`font-semibold ${good ? 'text-emerald-300' : val >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
      {val.toFixed(0)}%
    </span>
  );
};

export default DriverPerformancePage;
